import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { storage } from '../utils/storage';
import { createAppTheme } from './muiTheme';
import nrtFontUrl from '../fonts/NRT-Reg.ttf';

const ThemeContext = createContext();

const FONT_FAMILY = 'NRT';

const lightThemeBase = {
  container: { backgroundColor: '#FBF7F0' },
  section: { backgroundColor: '#FFF' },
  itemText: { color: '#333' },
  itemText2: { color: '#B8894A' },
  itemText3: { color: '#FFF' },
  modal: {
    backgroundColor: '#FFF',
    textColor: '#1E293B',
    primaryBackground: '#B8894A',
    secondaryText: '#7A6B55',
  },
};

const darkThemeBase = {
  container: { backgroundColor: '#121212' },
  section: { backgroundColor: '#1e1e1e' },
  itemText: { color: '#FFFFFF' },
  itemText2: { color: '#D4A574' },
  itemText3: { color: '#FFFFFF' },
  modal: {
    backgroundColor: '#1E293B',
    textColor: '#F8FAFC',
    primaryBackground: '#B8894A',
    secondaryText: '#94A3B8',
  },
};

export function ThemeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [themeVariant, setThemeVariant] = useState('light');

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const savedTheme = await storage.getItem('userTheme');
        if (savedTheme !== null) {
          const normalized = savedTheme === 'dark' ? 'dark' : 'light';
          setThemeVariant(normalized);
          setIsDarkMode(normalized === 'dark');
        }
      } catch (error) {
        console.error('Error loading theme preference:', error);
      }
    };
    loadPreferences();
  }, []);

  const setTheme = async (variant) => {
    const normalized = variant === 'dark' ? 'dark' : 'light';
    setThemeVariant(normalized);
    setIsDarkMode(normalized === 'dark');
    try {
      await storage.setItem('userTheme', normalized);
    } catch (error) {
      console.error('Failed to save theme preference', error);
    }
  };

  const toggleTheme = async () => {
    const nextVariant = isDarkMode ? 'light' : 'dark';
    setIsDarkMode(!isDarkMode);
    setThemeVariant(nextVariant);
    try {
      await storage.setItem('userTheme', nextVariant);
    } catch (error) {
      console.error('Failed to save theme preference', error);
    }
  };

  const baseTheme = isDarkMode ? darkThemeBase : lightThemeBase;

  const currentTheme = {
    ...baseTheme,
    container: { ...baseTheme.container, fontFamily: FONT_FAMILY },
    itemText: { ...baseTheme.itemText, fontFamily: FONT_FAMILY },
    itemText2: { ...baseTheme.itemText2, fontFamily: FONT_FAMILY },
  };

  const muiTheme = useMemo(() => {
    const theme = createAppTheme(themeVariant);
    return {
      ...theme,
      typography: {
        ...theme.typography,
        fontFamily: `'${FONT_FAMILY}', sans-serif`,
      },
    };
  }, [themeVariant]);

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        themeVariant,
        toggleTheme,
        setTheme,
        currentTheme,
        fontFamily: FONT_FAMILY,
      }}
    >
      <MuiThemeProvider theme={muiTheme}>
        <GlobalStyles
          styles={`
            @font-face {
              font-family: 'NRT';
              src: url(${nrtFontUrl}) format('truetype');
              font-weight: normal;
              font-style: normal;
              font-display: swap;
            }
          `}
        />
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
